import React, {useContext, useState, useRef, useEffect, useImperativeHandle, forwardRef} from 'react';
import classNames from 'classnames/bind';
import Field from 'core/ui/forms/field';
import {status} from 'hooks/useNotificationsStore';
import SessionContext from 'SessionContext'
import RequiredFields from "../../../partials/required-fields";
import RequiredLocationFields from "../../../partials/required-location-fields";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Collapse, Label, Input, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Alert } from 'reactstrap';
import ProgressLoader from "../../../partials/progress-loader";
import InfoPopover from "../info-popover";
import * as XLSX from 'xlsx';

const MB = 1024 * 1024;
const chunk_size = 9 * MB;

const mime_types = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};

const MAX_XLSX_SIZE = 25 * 1024 * 1024; // 25 MB

export const loaderStyle = {
  position: 'absolute',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  background: 'gray',
  zIndex: '10011',
  opacity: '0.4',
};

export const animatedStyle = {
  display: 'inline-block',
  position: 'relative',
  width: '60px',
  left: '20px',
  top: '-10px',
}
export const textStyle = {
  position: 'absolute',
  top: '4%',
  left: '33%',
  width: '33%',
  padding: '3em 0',
  textAlign: 'center',
  zIndex: '10022',
  fontWeight: 'bold',
  color: '#4a65a5',
  background: 'white'
}

export default function FileUploader(allProps) {
  const state = React.useState({error: ""});
  const [errorModalIsOpen, setErrorIsOpen] = React.useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showXlsxErrorMessage, setShowXlsxErrorMessage] = useState(false);
  const sheetSelectorRef = useRef(null);

  function toggleCollapse() {
    setIsOpen(!isOpen);
  }

  function openErrorModal(error) {
    state[0].error=error;
    setErrorIsOpen(true);
  }

  function getError() {
    let updatedError = state[0].error.replace(/Error: /gi, "");
    return updatedError;
  }

  function closeErrorModal() {
    state.error="";
    setErrorIsOpen(false);
  }
  const [dropActive, setDropActive] = useState();
  const session = useContext(SessionContext);
  const access = session.appState.userCan;
  const uploadButton = useRef(null);
  const [bypassInfo, setBypassInfo] = useState(false);

  async function uploadFile(file, onStart, abortFlag, isLocationUpload, onProgress){
    setIsUploading(true);
    
    const fileInfo = await filePreload(file);

    return new Promise( async function(resolve, reject) {
      let uploaded_size = 0;
      let failed = false;

      // First, create the file and get a file_id
      let response
      try {
        let target = 'upload/create'
        if (isLocationUpload) {
          target = target+'Location';
        }
        response = await session.command(target, {
          name: file.name, 
          size: file.size, 
          file_type: fileInfo.type || file.type,
          selected_page_index: fileInfo.index || 0, 
          bypass_ssn_cc_check: bypassInfo 
        },{defaultErrorHandling: false});
      } catch (e) {
        console.error(e);
        openErrorModal(e.toString());
        reject()
      }

      file.id = response.id;

      onStart && onStart(file);

      const _onProgress = (uploading, {loaded})=> onProgress({loaded: uploaded_size + loaded, total: file.size, uploading});

      for(let [chunk, has_more, is_first_chunk] of chunk_file(file, chunk_size)){
        // Construct a FormData so we can send a multipart form request
        const data = new FormData();
        data.append('id', file.id);
        data.append('has_more', has_more);
        data.append('is_first_chunk', is_first_chunk);
        data.append('chunk', chunk);

        try {
          await session.command('upload/append', data,
          {
            onProgress: _onProgress.bind(null, true),
            onLoad: _onProgress.bind(null, false),
          },{defaultErrorHandling: false})
          // eslint-disable-next-line
          .then(response => {
            if(response.soft_errors?.length > 0){
              failed = true;
              abortFlag && abortFlag(true);
              openErrorModal(response.soft_errors.join('\n'));
              reject();
            }
          });
          uploaded_size += chunk.size;
        } catch (e) {
          openErrorModal(e.toString());
          console.error(`Error: ${JSON.stringify(e)}`);
          failed = true;
          abortFlag && abortFlag(true);
          reject();
        }
      }
      if (!failed) {
        setIsUploading(false);
        resolve();
      } else {
        setIsUploading(false);
      }
    });
  }

  function* chunk_file(file, chunk_size){
    let from = 0;
    while(from < file.size){
      const to = from + chunk_size,
            chunk = file.slice(from, to),
            is_first_chunk = from === 0,
            has_more = to < file.size;
      yield [chunk, has_more, is_first_chunk];
      from += chunk_size;
    }
  }

  function onDragEnter(ev){
    ev.preventDefault();
    const dt = ev.dataTransfer,
          accept_mime_types = (accept || '').split(',').map((a)=> mime_types[a]);

    if (dt.types && (dt.types.indexOf ? dt.types.indexOf('Files') > -1 : dt.types.contains('Files'))) {
      const valid_files = Array.from(dt.items)
          .filter((i)=> i.kind === 'file'
              && (!accept
                  || accept_mime_types.indexOf(i.type) > -1
                  || accept.indexOf(i.type) > -1 )
          );
          setDropActive(valid_files.length > 0)
    }
    setDropActive(true)
  }

  function onClickPlus() {
    uploadButton.current.click();
  }

  function onDragLeave(ev){
    ev.preventDefault();
    setDropActive(false)
  }

  function onDrop(ev){
    ev.preventDefault();
    setDropActive(false)

    const dt = ev.dataTransfer;
    const files = dt.files ? [].map.call(dt.files, f => f) : dt.files;
    uploadFiles(files);
  }

  function onChooseFiles(ev){
    uploadFiles(Array.from(ev.target.files));
    ev.target.value = null;
  }

  async function handleChooseSheet(worksheets) {
    // Example: dynamic options (could come from props or API)
    const { ok, value, index } = await sheetSelectorRef.current.show({
      title: "Select Worksheet",
      message: "Select a worksheet to process for this upload.",
      options: worksheets, // or [{value:'id',label:'Nice Label'}, ...]
      initialValue: "", // optional
    });

    if (!ok) {
      throw new Error("User cancelled");
    }
    return {ok, value, index};
  };

  async function filePreload(file) {
    let fileType;
    if(file.type === mime_types.xlsx) {
      fileType = "XLSX";
    }else if(file.type === mime_types.csv) {
      fileType = "CSV";
    }else{
      fileType = "TXT";
    }

    // TODO: any upfront checks for security or data issues will go here as needed.

    if (fileType === "XLSX") {
      if (file.size > MAX_XLSX_SIZE) {
        setShowXlsxErrorMessage(true);
        throw new Error("XLSX file exceeds 25MB limit");
      }
      const reader = new FileReader();
      const data = await new Promise((resolve, reject) => {
        reader.onload = (e) => resolve(new Uint8Array(e.target.result));
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
      const workbook = XLSX.read(data, { type: 'array' });
      if(workbook.SheetNames.length > 1) {
        const {ok, value, index} = await handleChooseSheet(workbook.SheetNames);
        if(ok){
          return { name: value, index: index, type: fileType };
        }else{
          throw new Error("User cancelled");
        }
      } else{
        // if only a single worksheet
        return { name: file.name, index: 0, type: fileType };
      }
    } else{
      // CSV or TXT
      return { name: file.name, index: 0, type: fileType };
    }
  }

  function uploadFiles(files){
    const {onFileUploadStart, onFileUploadProgress, onFileUploadComplete, abortFlag, isLocationUpload} = allProps;

    files.forEach( async function(file){      
      const notification_handle = session.notificationsStore.add(file.name, status.working, 'Uploading', 0);

      try {
        let failed = false;
        onFileUploadComplete && onFileUploadComplete(false);
        await uploadFile(file, onFileUploadStart, abortFlag, isLocationUpload, (progress)=> {
          const percent = Math.min(100, Math.ceil(100 * (progress.loaded / progress.total)));
          session.notificationsStore.update(notification_handle, {percent, details: progress.uploading ? 'Uploading': 'Encrypting'});
          onFileUploadProgress && onFileUploadProgress(file, progress);
        }).catch( function (e) {
          failed = true;
          if(e !== "User cancelled") {
            session.notificationsStore.update(notification_handle,
              { status: status.error,
                details: 'Upload Error'
              });
          }
          setIsUploading(false);
          onFileUploadComplete && onFileUploadComplete(true);
        });

        if (!failed) {
          session.notificationsStore.update(notification_handle,
              { status: status.complete,
                details: 'Upload Complete',
                percent: 100});
          onFileUploadComplete && onFileUploadComplete(true);
        }
      } catch (e) {
        session.notificationsStore.update(notification_handle,
          { status: status.error,
            details: 'Upload Error'
          });
        onFileUploadComplete && onFileUploadComplete(true);
      }
    });
  }

  useEffect(() => { 
    setTimeout(() => { window.dispatchEvent(new Event('resize')) }, 500);
    // deselect the checkbox when user closes uploader
    if(!isOpen) {
      setBypassInfo(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCheckBypass = (ev) => {
    ev && ev.stopPropagation();
    setBypassInfo(ev.target.checked);
  }

  const {children, className, multiple, accept, isLocationUpload, ...props} = allProps;
  delete props.onFileUploadStart;
  delete props.onFileUploadComplete;
  delete props.onFileUploadProgress;
  return (
    <>
      <div {...props}
          className={classNames('file-uploader', {'is-active': dropActive, 'is-closed': !isOpen})}
          onDragEnter={onDragEnter} onDragOver={onDragEnter}
          onDragLeave={onDragLeave} onDrop={onDrop}>

        <div className={classNames('upload-control', {'is-open': isOpen})} >
          <Button color="light" size="sm" onClick={toggleCollapse}>
            {(isOpen) ? "Close File Uploader" : "Open File Uploader"} <i className={(isOpen) ? "bi bi-chevron-up" : "bi bi-chevron-down"}></i>
          </Button>
        </div>
        <Collapse isOpen={isOpen}>
          <i className="fa fa-circle-plus" onClick={onClickPlus}></i>
          
          {/* TODO: when drag and drop is ready, enable this text  */}
          {/* <p> */}
           {/*   Drag and Drop a file here, or Select a file below  */}
          {/* </p> */}
          <Field label="Choose File">
            <input type="file" multiple={multiple} accept={accept}
              ref={uploadButton} onChange={onChooseFiles}/>
          </Field>

          {access("administration") && 
            <p className="check-info">
              <Input
                  id="info"
                  name="check"
                  type="checkbox"
                  checked={bypassInfo}
                  onChange={handleCheckBypass}
              />
              <Label check for="info">
                Bypass check for SSN and Credit Card Info
              </Label>
            </p>
          }

          <Alert color="light" className="file-types-info">
          {!isLocationUpload ? (
            <>
              <div>
                <p><strong>Supported file types:</strong></p> 
                  <div className="upload-types">
                    <span>
                      <p>.xlsx </p>
                      <InfoPopover 
                        title=".XLSX Upload Requirements"
                        message={`<p>MS Excel Spreadsheet. Only one user selected sheet/tab will be used for upload.</p><p>Sheet must have a header line<br /> on row A.</p><p>Total file size must not exceed 25MB, approximately 400,000 total records.</p>`}
                      />
                    </span>
                    <span>
                      <p>.csv </p>
                      <InfoPopover 
                        title=".CSV Upload Requirements"
                        message={`<p>File must have a header line and all values must be comma delimited.</p><p>Total record count must not exceed 5,000,000.</p>`}
                      />
                    </span>
                    <span>
                      <p>.txt </p>
                      <InfoPopover 
                        title=".TXT Upload Requirements"
                        message={`<p>File must have a header line and all values must use the same delimiter.</p><p>Supported delimiters are comma, pipe, and tab.</p><p>Total record count must not exceed 5,000,000.</p>`}
                      />
                    </span>
                  </div>
              </div>
              <RequiredFields/>
            </>
          ):(
          <>
            <div>
              <p><strong>Supported file types:</strong></p> 
                <div className="upload-types">
                  <span>
                    <p>.csv </p>
                    <InfoPopover 
                      title=".CSV Upload Requirements"
                      message="File must have a header line and all values must be comma delimited."
                    />
                  </span>
                </div>
            </div>
            <RequiredLocationFields/>
          </>
          )} 
          </Alert>

          {isUploading && (<>
            <div style={loaderStyle} />
            <div style={textStyle}>
              Uploading file... please wait 
              <span style={animatedStyle}><ProgressLoader /></span>
            </div>
          </>)}
          <ConfirmSelectModal ref={sheetSelectorRef} />
          <div>
            <Modal centered
                  isOpen={errorModalIsOpen}
                  toggle={() => closeErrorModal()}
                  backdrop>
                  <ModalHeader toggle={() => closeErrorModal()}>
                      <i className='bi bi-exclamation-triangle-fill'></i>{' '}Error Uploading File
                  </ModalHeader>
                    <ModalBody>
                      <p>{getError()}</p>
                    </ModalBody>
                  <ModalFooter>
                    <Button onClick={() => closeErrorModal()}>Close</Button>
                  </ModalFooter>
            </Modal>
          </div>
        </Collapse>
        <Modal centered isOpen={showXlsxErrorMessage} backdrop>
          <ModalHeader>
            <i className='bi bi-exclamation-triangle-fill'></i>{' '}Error Uploading File
          </ModalHeader>
            <ModalBody>
              <Alert color="warning">
                Excel Files cannot exceed 25MB. Please reduce the file size or convert to csv or txt to proceed. Contact Customer Support for further assistance.
              </Alert>
            </ModalBody>
          <ModalFooter>
            <Button onClick={() => setShowXlsxErrorMessage(false)}>Close</Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
}

const ConfirmSelectModal = forwardRef((_, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("Confirm Selection");
  const [message, setMessage] = useState("");
  const [options, setOptions] = useState([]); // [{value, label}] or strings
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const [resolver, setResolver] = useState(null);

  // Show the modal and return a Promise that resolves with { ok, value }
  useImperativeHandle(ref, () => ({
    /**
     * show(config): Promise<{ok:boolean, value:any}>
     * config = { title?, message?, options: (string[]|{value,label}[]), initialValue?, placeholder? }
     */
    show: (config = {}) =>
      new Promise((resolve) => {
        const {
          title = "Confirm Selection",
          message = "",
          options = [],
          initialValue = {value: 0, label: ""},
        } = config;

        setTitle(title);
        setMessage(message);
        setOptions(options);
        setSelected(initialValue ?? {});
        setResolver(() => resolve);
        setIsOpen(true);
      }),
  }));

  // Reset resolver if modal closes unexpectedly
  useEffect(() => {
    if (!isOpen && resolver) {
      // If closed without explicit confirm/cancel, treat as cancel
      resolver({ ok: false, value: null });
      setResolver(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const closeWith = (ok) => {
    if (resolver) {
      resolver({ ok, value: ok ? selected.label : null, index: ok ? selected.value : 0 });
      setResolver(null);
    }
    setIsOpen(false);
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const normalized = Array.isArray(options)
    ? options.map((o, i) =>
        typeof o === "string" ? { value: i, label: o } : o
      )
    : [];

  const canConfirm = String(selected).length > 0;

  return (
    <Modal isOpen={isOpen} toggle={() => closeWith(false)} centered backdrop>
      <ModalHeader toggle={() => closeWith(false)}>{title}</ModalHeader>
      <ModalBody>
        {message && <p className="mb-3">{message}</p>}

        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
          <DropdownToggle caret >
            {selected?.label ? selected.label : "Select a worksheet"}
          </DropdownToggle>
          <DropdownMenu>
            {normalized.map((opt) => (
              <DropdownItem
                key={String(opt.value)}
                active={opt.value === selected.value}
                onClick={() => setSelected(opt)}
              >
                {opt.label}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </ModalBody>
      <ModalFooter>
        <Button onClick={() => closeWith(false)}>
          Cancel
        </Button>
        <Button className="promote" onClick={() => closeWith(true)} disabled={!canConfirm}>
          Confirm
        </Button>
      </ModalFooter>
    </Modal>
  );
});
